import { FulfillmentGroupData } from "@redotech/redo-fulfillment-group-common/fulfillment-group";
import { OrderDetailsInfo } from "@redotech/redo-merchant-app-common/order-details-info";
import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useState,
} from "react";

export interface PreloadedOrderInfo {
  orderInfo: OrderDetailsInfo;
  fulfillmentGroupInfo: FulfillmentGroupData[];
}

interface OrderNavigationContextType {
  fulfillmentGroupInfo: { id: string; orderId: string; isRedoOrder: boolean }[];
  orderPagePrefetchedInfo: Record<string, PreloadedOrderInfo>;
  resetOrderPagePrefetchedInfo: (
    orderInfo: Record<string, PreloadedOrderInfo>,
  ) => void;
  returnUrl: string | undefined;
  initialize: (
    fulfillmentGroupInfo: {
      id: string;
      orderId: string;
      isRedoOrder: boolean;
    }[],
    returnUrl: string,
  ) => void;
}

export const OrderNavigationContext = createContext<
  OrderNavigationContextType | undefined
>(undefined);

export function useOrderNavigation() {
  const context = useContext(OrderNavigationContext);
  if (!context) {
    throw new Error(
      "useOrderNavigation must be used within an OrderNavigationProvider",
    );
  }
  return context;
}

interface OrderNavigationProviderProps {
  children: ReactNode;
}

export const OrderNavigationProvider = ({
  children,
}: OrderNavigationProviderProps) => {
  const [fulfillmentGroupInfo, setFulfillmentGroupInfo] = useState<
    { id: string; orderId: string; isRedoOrder: boolean }[]
  >([]);
  const [orderPagePrefetchedInfo, setOrderPagePrefetchedInfo] = useState<
    Record<string, PreloadedOrderInfo>
  >({});
  const [returnUrl, setReturnUrl] = useState<string | undefined>(undefined);

  const initialize = useCallback(
    (
      fulfillmentGroupInfo: {
        id: string;
        orderId: string;
        isRedoOrder: boolean;
      }[],
      returnUrl: string,
    ) => {
      setFulfillmentGroupInfo(fulfillmentGroupInfo);
      setReturnUrl(returnUrl);
    },
    [],
  );

  const resetOrderPagePrefetchedInfo = useCallback(
    (orderInfo: Record<string, PreloadedOrderInfo>) => {
      setOrderPagePrefetchedInfo(orderInfo);
    },
    [],
  );

  const value: OrderNavigationContextType = {
    orderPagePrefetchedInfo,
    fulfillmentGroupInfo,
    returnUrl,
    initialize,
    resetOrderPagePrefetchedInfo,
  };

  return (
    <OrderNavigationContext.Provider value={value}>
      {children}
    </OrderNavigationContext.Provider>
  );
};
